/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useRef, useState, useEffect, useMemo } from 'react'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import { RestrictToHorizontalAxis } from '@dnd-kit/abstract/modifiers'
import { arrayMove } from '@dnd-kit/helpers'
import {
    PointerSensor,
    PointerActivationConstraints,
    AutoScroller,
} from '@dnd-kit/dom'
import { normalizeAccents } from 'bam-node-baseball-savant/utils/normalize-accents'
import { appSettings } from 'bam-node-baseball-savant/leaderboard/appSettings'
import { teamMap } from 'bam-node-baseball-savant/constants/teams'
import {
    SEARCH_PLAYER_COMPONENT_BUFFER,
    SEARCH_PLAYER_COMPONENT_WIDTH,
    SEARCH_PLAYER_STAGING_HEIGHT,
    DARKER_SAVANT_GREEN,
    // MAX_TOTAL_PLAYERS,
    POSITION_MAP,
} from './utils/constants.ts'
import { CompToolPlayer, PlayerSearchProps } from './utils/types.ts'
import { useDimensions } from './utils/responsive-dimensions.tsx'
import { getUniqueId, getPosString } from './utils/helpers.ts'
import PlayerSearchSelectedCard from './PlayerSearchSelectedCard.tsx'
import SortableWrapper from './SortableWrapper.tsx'
import { getSimilarPlayers } from './utils/similar-players.ts'

const validYears = appSettings.SETTINGS_COMPARISON_TOOL.years
const BIG_PAGE_SIZE = 10

type TabView = 'all' | 'trending' | 'suggested'

export default function PlayerSearch({
    searchRef,
    data,
    preloadedData,
    trendingBatters,
    trendingPitchers,
    onAdd,
}: PlayerSearchProps) {
    const { MAX_TOTAL_PLAYERS, isMobile } = useDimensions()

    const PAGE_SIZE = isMobile ? 6 : 7

    const preloadedPlayers = preloadedData.players

    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const prevPlayerCount = useRef(preloadedPlayers.length)

    const [searchFilter, setSearchFilter] = useState('')
    const [yearFilter, setYearFilter] = useState(2026)
    const [pageNumber, setPageNumber] = useState(0)
    const [teamFilter, setTeamFilter] = useState('0')
    const [posFilter, setPosFilter] = useState(0)
    const [hoveredPlayer, setHoveredPlayer] = useState<string | null>(null)
    const [flashingPlayer, setFlashingPlayer] = useState<string | null>(null)
    const [activeTab, setActiveTab] = useState<TabView>('trending')

    const preloadedPlayerObjs = preloadedPlayers
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((playerObj) => {
            const [id, year, type] = playerObj.id.split('-')
            const playerData = data.find(
                (d) =>
                    Number(d.id) === Number(id) &&
                    Number(d.year) === Number(year) &&
                    d.grouping_cat.toLowerCase() === type
            ) as CompToolPlayer

            const dataObj = preloadedData.dataToUse.find(
                (d) => d.uniqueId === playerObj.id
            )

            if (dataObj) {
                playerData.teams = dataObj.teams
            }

            return { playerData, displayOrder: playerObj.displayOrder }
        })

    const [selectedPlayers, setSelectedPlayers] =
        useState<{ playerData: CompToolPlayer; displayOrder: number }[]>(
            preloadedPlayerObjs
        )
    const defaultType = preloadedPlayerObjs.length
        ? preloadedPlayerObjs[0].playerData.grouping_cat.toLowerCase()
        : ''
    const [typeFilter, setTypeFilter] = useState(defaultType)

    useEffect(() => {
        if (selectedPlayers.length) {
            setTypeFilter(
                selectedPlayers[0]?.playerData.grouping_cat.toLowerCase()
            )
        }
    }, [selectedPlayers])

    useEffect(() => {
        setSelectedPlayers(preloadedPlayerObjs)
    }, [preloadedPlayers])

    useEffect(() => {
        setPageNumber(0)
    }, [activeTab])

    useEffect(() => {
        if (selectedPlayers.length > prevPlayerCount.current) {
            if (scrollContainerRef.current) {
                setTimeout(() => {
                    if (scrollContainerRef.current) {
                        scrollContainerRef.current.scrollTo({
                            left: scrollContainerRef.current.scrollWidth,
                            behavior: 'smooth',
                        })
                    }
                }, 10)
            }
        }
        prevPlayerCount.current = selectedPlayers.length
    }, [selectedPlayers.length])

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const shouldOpenSearch = urlParams.get('openSearch') === 'true'
        const hasActiveTab = urlParams.get('activeSearchTab')

        if (shouldOpenSearch) {
            if (!hasActiveTab || hasActiveTab === 'suggested') {
                setActiveTab('suggested')
            } else if (hasActiveTab === 'all') {
                setActiveTab('all')
            } else {
                setActiveTab('trending')
            }
        }
    }, [])

    const handleAddBatchRef = useRef(handleAddBatch)
    useEffect(() => {
        handleAddBatchRef.current = handleAddBatch
    })

    useEffect(() => {
        if (!searchRef.current) {
            return
        }

        searchRef.current.addEventListener('toggle', (e) => {
            if (e.newState !== 'closed') {
                return
            }

            handleAddBatchRef.current()
        })
    }, [searchRef])

    const sensors = useMemo(
        () => [
            PointerSensor.configure({
                activationConstraints: [
                    new PointerActivationConstraints.Distance({
                        value: 8,
                    }),
                ],
            }),
        ],
        []
    )

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleDragOver = (event: any) => {
        const { operation } = event
        const { source, target } = operation

        if (isSortable(source) && isSortable(target)) {
            const overIndex = target.sortable.index
            const activeIndex = source.sortable.index

            if (activeIndex !== overIndex) {
                setSelectedPlayers((prev) => {
                    const currentVisualOrder = [...prev].sort(
                        (a, b) => a.displayOrder - b.displayOrder
                    )
                    const reordered = arrayMove(
                        currentVisualOrder,
                        activeIndex,
                        overIndex
                    )

                    return reordered.map((p, i) => ({
                        ...p,
                        displayOrder: i,
                    }))
                })
            }
        }
    }

    const similarPlayers = useMemo(
        () =>
            selectedPlayers.length > 0
                ? getSimilarPlayers(data, selectedPlayers[0]?.playerData)
                : [],
        [data, selectedPlayers]
    )

    const needle = normalizeAccents(searchFilter)

    const filtered = useMemo(() => {
        let baseSlice = [...data]

        if (activeTab === 'trending') {
            baseSlice =
                typeFilter === 'pitcher'
                    ? [...trendingPitchers]
                    : [...trendingBatters]

            baseSlice = baseSlice.slice(0, -1)
        } else if (activeTab === 'suggested') {
            baseSlice = [...similarPlayers]
        }

        if (activeTab !== 'all') {
            return baseSlice
        }

        return baseSlice
            .sort((a, b) => {
                // Most recent first
                if (a.year !== b.year) {
                    return a.year < b.year ? 1 : -1
                }

                if (a.name.toUpperCase() !== b.name.toUpperCase()) {
                    return a.name.toUpperCase() > b.name.toUpperCase() ? 1 : -1
                }

                // same name, be consistent
                return Number(a.id) - Number(b.id)
            })
            .filter((d) => {
                if (!yearFilter) {
                    return true
                }
                return d.year === yearFilter
            })
            .filter((d) => {
                if (typeFilter === '') {
                    return true
                }

                if (typeFilter === 'pitcher') {
                    return (
                        d.grouping_cat.toLowerCase() === 'pitcher' &&
                        (Number(d.pos) === 1 ||
                            d.pos === 'Y' ||
                            Number(d.pos) === 10)
                    )
                }

                return d.grouping_cat.toLowerCase() === 'batter'
            })
            .filter((d) => {
                if (teamFilter === '0') {
                    return true
                }
                return d.teams?.includes(Number(teamFilter))
            })
            .filter((d) => {
                if (posFilter === 0) {
                    return true
                }
                return Number(d.pos) === posFilter
            })
            .filter((d) => {
                if (searchFilter === '') {
                    return true
                }
                const haystack = normalizeAccents(d.name)
                return haystack.includes(needle)
            })
    }, [
        data,
        similarPlayers,
        activeTab,
        yearFilter,
        typeFilter,
        teamFilter,
        searchFilter,
        posFilter,
        needle,
    ])

    const pageSizeToUse = activeTab === 'all' ? PAGE_SIZE : BIG_PAGE_SIZE

    const sliced = filtered.slice(
        pageNumber * pageSizeToUse,
        pageNumber * pageSizeToUse + pageSizeToUse
    )

    const pages = Math.ceil(filtered.length / pageSizeToUse)

    function randomize() {
        setSelectedPlayers([])

        const allData = [...data]
        const typeToCompare = typeFilter !== 'pitcher' ? 'batter' : 'pitcher'
        let thisYearData = allData.filter(
            (d) =>
                Number(d.year) === Number(yearFilter) &&
                d.grouping_cat.toLowerCase() === typeToCompare
        )

        if (typeToCompare === 'pitcher') {
            thisYearData = thisYearData.filter((d) => d.pos === '1')
        }

        const numTotalPlayers = thisYearData.length
        const numRandomPlayers = 6

        const randomIndices = new Set<number>()

        while (randomIndices.size < numRandomPlayers) {
            const randomInt = Math.floor(Math.random() * numTotalPlayers)
            randomIndices.add(randomInt)
        }

        const randomArr = Array.from(randomIndices)
        const randomPlayers: {
            playerData: CompToolPlayer
            displayOrder: number
        }[] = []

        for (let i = 0; i < numRandomPlayers; i++) {
            randomPlayers.push({
                playerData: thisYearData[randomArr[i]],
                displayOrder: i,
            })
        }

        setSelectedPlayers(randomPlayers)
    }

    function handleSelect(d: CompToolPlayer) {
        if (
            selectedPlayers.map((player) => player.playerData).includes(d) ||
            selectedPlayers.length === MAX_TOTAL_PLAYERS
        ) {
            return
        }

        const latestSelect = Math.max(
            ...selectedPlayers.map((player) => player.displayOrder)
        )
        const newSelects = [
            ...selectedPlayers,
            { playerData: d, displayOrder: latestSelect + 1 },
        ]

        setSelectedPlayers(newSelects)
    }

    function handleFilter(
        type: 'year' | 'type' | 'search' | 'team' | 'pos',
        value: string | number
    ) {
        switch (type) {
            case 'year':
                setYearFilter(Number(value))
                break
            case 'type':
                setTypeFilter(String(value))
                break
            case 'team':
                setTeamFilter(String(value))
                break
            case 'pos':
                setPosFilter(Number(value))
                break
            case 'search':
            default:
                setSearchFilter(String(value))
                break
        }

        setPageNumber(0)
    }

    function handleAddBatch() {
        if (searchRef.current) {
            searchRef.current.hidePopover()
        }

        onAdd(selectedPlayers)
        setYearFilter(appSettings.SETTINGS_COMPARISON_TOOL.currentYear)
        setPageNumber(0)
    }

    function removePlayer(player: CompToolPlayer) {
        setSelectedPlayers(
            selectedPlayers.filter((pl) => pl.playerData !== player)
        )
        setHoveredPlayer(null)
    }

    function handleTeamClick(team: string) {
        if (teamFilter === team) {
            setTeamFilter('0')
        } else {
            setTeamFilter(team)
        }

        setPageNumber(0)
    }

    const prevPage = pageNumber !== 0
    const nextPage = pageNumber + 1 !== pages

    const svgWidth =
        selectedPlayers.length *
            (SEARCH_PLAYER_COMPONENT_BUFFER + SEARCH_PLAYER_COMPONENT_WIDTH) -
        SEARCH_PLAYER_COMPONENT_BUFFER

    function getTabStyle(tabName: TabView): React.CSSProperties {
        return {
            fontWeight: activeTab === tabName ? 'bold' : 'normal',
            color: activeTab === tabName ? '#ffffff' : '#aaaaaa',
            backgroundColor:
                activeTab === tabName ? DARKER_SAVANT_GREEN : 'transparent',
            paddingTop: '3px',
            paddingBottom: '3px',
        }
    }

    return (
        <div
            {...({
                popover: 'auto',
            } as React.HTMLAttributes<HTMLDivElement>)}
            id="player-search"
            ref={searchRef}
            className="comp-tool-popover player-search-popover"
        >
            <style>{`
                .player-search-popover {
                    flex-direction: column;
                    height: 80vh;
                    max-height: 690px;
                    box-sizing: border-box;
                }

                .player-search-popover:popover-open {
                    display: flex !important;
                }

                ${
                    isMobile
                        ? `
                        .player-search-dropdown-wrapper {
                            flex-wrap: wrap;
                            gap: 0.25rem;
                        }
                        .player-search-dropdown {
                            flex-grow: 1;
                            flex-basis: 30%; 
                        }
                        .player-search-dropdown select {
                            font-size: 12px;
                            padding: 2px 4px;
                            height: 28px;
                        }
                        #team-search-dropdown {
                            margin-bottom: 0.5rem;
                        }
                        #pos-search-dropdown {
                            margin-bottom: 0.5rem;
                        }
                        #year-search-dropdown {
                            margin-bottom: 0.5rem;
                        }
                        #type-search-dropdown {
                            margin-bottom: 0.5rem;
                        }
                    `
                        : ''
                }
            `}</style>

            <div style={{ flex: 1, overflowY: 'auto', paddingRight: '4px' }}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '0.5rem',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2rem',
                        }}
                    >
                        <h3
                            style={{
                                fontSize: isMobile ? '18px' : 'auto',
                                margin: 0,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Select Player(s)
                        </h3>
                        <button
                            type="button"
                            className="btn btn-default"
                            onClick={() => {
                                setSelectedPlayers([])
                                setTypeFilter(
                                    typeFilter === 'pitcher'
                                        ? 'batter'
                                        : 'pitcher'
                                )
                                setPageNumber(0)
                            }}
                            style={{
                                borderRadius: '4px',
                                padding: '2px 4px 2px 4px',
                                fontSize: '12px',
                                marginTop: '4px',
                                height: isMobile ? '24px' : 'auto',
                            }}
                        >
                            ⇄ Switch to{' '}
                            {typeFilter === 'pitcher' ? 'Batters' : 'Pitchers'}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            const popover =
                                document.getElementById('player-search')
                            if (popover && 'hidePopover' in popover) {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                ;(popover as any).hidePopover()
                            }
                        }}
                        className="modal-close-button"
                        aria-label="Close Popover"
                    >
                        &times;
                    </button>
                </div>

                <div
                    className="player-search-tabs"
                    style={{
                        display: 'flex',
                        marginBottom: '8px',
                        width: '100%',
                        paddingTop: '2px',
                        paddingBottom: '2px',
                    }}
                >
                    <button
                        type="button"
                        style={getTabStyle('all')}
                        className="player-search-tab-button"
                        onClick={() => setActiveTab('all')}
                    >
                        All Players
                    </button>
                    <button
                        type="button"
                        style={getTabStyle('trending')}
                        className="player-search-tab-button"
                        onClick={() => setActiveTab('trending')}
                    >
                        Trending
                    </button>
                    <button
                        type="button"
                        style={getTabStyle('suggested')}
                        className="player-search-tab-button"
                        onClick={() => setActiveTab('suggested')}
                    >
                        Suggested
                    </button>
                </div>

                {activeTab === 'all' ? (
                    <>
                        <div>
                            <input
                                className="form-control comp-tool-search"
                                type="text"
                                value={searchFilter}
                                onChange={(e) =>
                                    handleFilter('search', e.target.value)
                                }
                                placeholder="Search by player name"
                            />
                        </div>
                        <div className="form-group player-search-dropdown-wrapper">
                            <div
                                className="savant-select player-search-dropdown"
                                id="year-search-dropdown"
                                data-title="Year"
                            >
                                <select
                                    id="ddlYearSearch"
                                    value={yearFilter}
                                    onChange={(e) =>
                                        handleFilter(
                                            'year',
                                            Number(e.target.value)
                                        )
                                    }
                                >
                                    <option value={0}>Any</option>
                                    <hr />
                                    {validYears.map((year) => (
                                        <option key={year} value={Number(year)}>
                                            {year}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div
                                className="savant-select player-search-dropdown"
                                id="type-search-dropdown"
                                data-title="Type"
                            >
                                <select
                                    id="ddlTypeSearch"
                                    value={typeFilter}
                                    onChange={(e) =>
                                        handleFilter('type', e.target.value)
                                    }
                                >
                                    <option
                                        value=""
                                        disabled={selectedPlayers.length > 0}
                                    >
                                        Any
                                    </option>
                                    <hr />
                                    <option
                                        value="batter"
                                        disabled={
                                            selectedPlayers.length > 0 &&
                                            selectedPlayers[0]?.playerData.grouping_cat.toLowerCase() ===
                                                'pitcher'
                                        }
                                    >
                                        Batter
                                    </option>
                                    <option
                                        value="pitcher"
                                        disabled={
                                            selectedPlayers.length > 0 &&
                                            selectedPlayers[0]?.playerData.grouping_cat.toLowerCase() ===
                                                'batter'
                                        }
                                    >
                                        Pitcher
                                    </option>
                                </select>
                            </div>
                            <div
                                className="savant-select player-search-dropdown"
                                id="team-search-dropdown"
                                data-title="Team"
                            >
                                <select
                                    id="ddlTeamSearch"
                                    value={teamFilter}
                                    onChange={(e) =>
                                        handleFilter('team', e.target.value)
                                    }
                                >
                                    <option value="0">Any</option>
                                    <hr />
                                    {Object.entries(teamMap)
                                        .sort(([, val1], [, val2]) =>
                                            val1.abbrev < val2.abbrev ? -1 : 1
                                        )
                                        .map(([key, value]) => (
                                            <option key={key} value={key}>
                                                {value.abbrev}
                                            </option>
                                        ))}
                                </select>
                            </div>
                            <div
                                className="savant-select player-search-dropdown"
                                id="pos-search-dropdown"
                                data-title="Pos"
                            >
                                <select
                                    id="ddlPosSearch"
                                    value={posFilter}
                                    onChange={(e) =>
                                        handleFilter('pos', e.target.value)
                                    }
                                >
                                    <option value={0}>Any</option>
                                    <hr />
                                    {Object.keys(POSITION_MAP)
                                        .sort((val1, val2) =>
                                            Number(val1) < Number(val2) ? -1 : 1
                                        )
                                        .map((key) => (
                                            <option
                                                key={key}
                                                value={Number(key)}
                                                disabled={
                                                    (selectedPlayers[0]?.playerData.grouping_cat.toLowerCase() ===
                                                        'batter' &&
                                                        Number(key) === 1) ||
                                                    (selectedPlayers[0]?.playerData.grouping_cat.toLowerCase() ===
                                                        'pitcher' &&
                                                        Number(key) !== 1)
                                                }
                                            >
                                                {POSITION_MAP[Number(key)]}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>
                    </>
                ) : (
                    ''
                )}

                <div>
                    {sliced.map((d) => {
                        const imageSize = 30
                        const uniqueId = getUniqueId(d)

                        const isSelected = selectedPlayers.some(
                            (p) => getUniqueId(p.playerData) === uniqueId
                        )
                        const isMaxedOut =
                            selectedPlayers.length >= MAX_TOTAL_PLAYERS
                        const isFlashing = flashingPlayer === uniqueId

                        let bgColor = 'white'
                        if (isFlashing) {
                            bgColor = 'rgba(57, 144, 152, 0.4)' // A Savant-green flash
                        } else if (isSelected) {
                            bgColor = '#e6e6e6' // Shaded grey for already selected
                        }

                        return (
                            <div
                                key={uniqueId}
                                className="player-search-element"
                                style={{
                                    background: bgColor,
                                    cursor:
                                        isSelected || isMaxedOut
                                            ? 'default'
                                            : 'pointer',
                                    opacity: isSelected ? 0.5 : 1,
                                }}
                                onClick={() => {
                                    if (isSelected) {
                                        removePlayer(d)
                                        return
                                    }

                                    if (isMaxedOut) {
                                        return
                                    }

                                    setFlashingPlayer(uniqueId)
                                    setTimeout(
                                        () => setFlashingPlayer(null),
                                        150
                                    )
                                    handleSelect(d)
                                }}
                            >
                                <img
                                    alt={d.name}
                                    src={`https://midfield.mlbstatic.com/v1/people/${
                                        d.id
                                    }/spots/${imageSize * 3}`}
                                    height={imageSize * 1}
                                    width={imageSize * 1}
                                />
                                <div className="player-search-element-name">
                                    {d.name}
                                </div>
                                <div className="player-search-element-teams">
                                    {d.teams?.map((team) => (
                                        <div
                                            key={team}
                                            className="player-search-element-team"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleTeamClick(String(team))
                                            }}
                                        >
                                            <img
                                                alt={String(team)}
                                                key={team}
                                                src={`https://www.mlbstatic.com/team-logos/${team}.svg`}
                                                width={12}
                                                height={12}
                                            />
                                        </div>
                                    ))}
                                </div>
                                {d.pos ? (
                                    <div className="player-search-element-pos">
                                        {getPosString(
                                            d.id,
                                            d.pos,
                                            d.grouping_cat as
                                                | 'Batter'
                                                | 'Pitcher'
                                        )}
                                    </div>
                                ) : (
                                    <div className="player-search-element-pos">
                                        &nbsp;
                                    </div>
                                )}
                                <div className="player-search-element-year">
                                    {d.year}
                                </div>
                                <div className="player-search-grouping-category">
                                    {d.grouping_cat}
                                </div>
                            </div>
                        )
                    })}
                </div>

                {activeTab === 'all' ? (
                    <div style={{ textAlign: 'center', margin: '0.7rem' }}>
                        <button
                            type="button"
                            onClick={() => setPageNumber(0)}
                            disabled={!prevPage}
                            style={{
                                cursor: prevPage ? 'pointer' : 'not-allowed',
                            }}
                        >
                            {'<<'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setPageNumber(pageNumber - 1)}
                            disabled={!prevPage}
                            style={{
                                cursor: prevPage ? 'pointer' : 'not-allowed',
                            }}
                        >
                            ←
                        </button>
                        <span
                            style={{
                                display: 'inline-block',
                                margin: '0 1rem',
                            }}
                        >
                            Page {pageNumber + 1}
                        </span>
                        <button
                            type="button"
                            onClick={() => setPageNumber(pageNumber + 1)}
                            disabled={!nextPage}
                            style={{
                                cursor: nextPage ? 'pointer' : 'not-allowed',
                            }}
                        >
                            →
                        </button>
                        <button
                            type="button"
                            onClick={() => setPageNumber(pages - 1)}
                            disabled={!nextPage}
                            style={{
                                cursor: nextPage ? 'pointer' : 'not-allowed',
                            }}
                        >
                            {'>>'}
                        </button>
                    </div>
                ) : (
                    ''
                )}

                {activeTab === 'trending' && (
                    <div style={{ textAlign: 'center', margin: '0.7rem' }}>
                        <button
                            type="button"
                            className="btn btn-default"
                            onClick={randomize}
                            style={{ padding: '5px' }}
                        >
                            Select Random Players!
                        </button>
                    </div>
                )}
            </div>

            <div style={{ flexShrink: 0, backgroundColor: 'inherit' }}>
                <hr
                    style={{ margin: '0.7rem', borderTop: '1px solid #555555' }}
                />
                <div>
                    <h5 style={{ fontSize: '16px' }}>Selected Player(s)</h5>
                </div>
                <div
                    ref={scrollContainerRef}
                    className="player-search-staging-box"
                    style={{
                        height: `${SEARCH_PLAYER_STAGING_HEIGHT}px`,
                    }}
                >
                    <DragDropProvider
                        sensors={sensors}
                        onDragOver={handleDragOver}
                        modifiers={[RestrictToHorizontalAxis]}
                        plugins={(defaults) =>
                            defaults.filter((plugin) => plugin !== AutoScroller)
                        }
                    >
                        <svg style={{ width: `${svgWidth}px`, height: '100%' }}>
                            {selectedPlayers
                                .sort((a, b) => a.displayOrder - b.displayOrder)
                                .map((player, i) => {
                                    const uniqueId = getUniqueId(
                                        player.playerData
                                    )
                                    const xVal =
                                        i *
                                        (SEARCH_PLAYER_COMPONENT_BUFFER +
                                            SEARCH_PLAYER_COMPONENT_WIDTH)

                                    return (
                                        <SortableWrapper
                                            key={uniqueId}
                                            id={uniqueId}
                                            index={i}
                                            offset={xVal}
                                            type="player-modal"
                                        >
                                            <g
                                                onMouseEnter={() =>
                                                    setHoveredPlayer(uniqueId)
                                                }
                                                onMouseLeave={() =>
                                                    setHoveredPlayer(null)
                                                }
                                            >
                                                <PlayerSearchSelectedCard
                                                    player={player.playerData}
                                                    uniqueId={uniqueId}
                                                />
                                                <g
                                                    transform={`translate(${
                                                        SEARCH_PLAYER_COMPONENT_WIDTH -
                                                        19
                                                    }, 2)`}
                                                    onPointerDown={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onMouseDown={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        removePlayer(
                                                            player.playerData
                                                        )
                                                    }}
                                                    className="remove-button-x-elem"
                                                    style={{
                                                        opacity:
                                                            hoveredPlayer ===
                                                            uniqueId
                                                                ? 1
                                                                : 0,
                                                        pointerEvents:
                                                            hoveredPlayer ===
                                                            uniqueId
                                                                ? 'auto'
                                                                : 'none',
                                                    }}
                                                >
                                                    <circle
                                                        cx={9}
                                                        cy={9}
                                                        r={9}
                                                        fill={
                                                            DARKER_SAVANT_GREEN
                                                        }
                                                        stroke={
                                                            DARKER_SAVANT_GREEN
                                                        }
                                                    />
                                                    <text
                                                        x={9}
                                                        y={13}
                                                        textAnchor="middle"
                                                        fontSize="12"
                                                        fontWeight="bold"
                                                        fill="white"
                                                        pointerEvents="none"
                                                    >
                                                        ✖
                                                    </text>
                                                </g>
                                            </g>
                                        </SortableWrapper>
                                    )
                                })}
                        </svg>
                    </DragDropProvider>
                </div>
                <div className="player-search-button-wrapper">
                    <button
                        type="button"
                        id="btn-add-players"
                        className="btn btn-default"
                        style={{ padding: '5px', marginRight: '0.5rem' }}
                        onClick={() => setSelectedPlayers([])}
                    >
                        <span style={{ fontSize: '13px' }}>Clear Players</span>
                    </button>
                    <button
                        type="button"
                        id="btn-add-players"
                        className="btn btn-default"
                        style={{ padding: '5px' }}
                        onClick={() => handleAddBatch()}
                    >
                        <span style={{ fontSize: '13px' }}>
                            Compare These Players!
                        </span>
                    </button>
                </div>
            </div>
        </div>
    )
}
